import { useEffect, useMemo, useState, type RefObject } from "react";
import type { Quest } from "@outpost/shared";
import {
  CHANNEL_FILTERS,
  CHANNEL_LABELS,
  GTM_MISSIONS,
  SEED_QUEST_CATALOG,
  XP_PER_QUEST,
  allDecadeStarts,
  catalogGlyphSrc,
  decadeEnd,
  decadeStart,
  isMinLevelLocked,
  lookoutXpProgress,
  matchSeedInQuests,
  questBrief,
  questSteps,
  questXp,
  findSeedForQuest,
  type QuestChannel,
  type SeedQuest,
} from "./questCatalog";

type CatalogRail = "needs" | "ready" | "done" | "locked";

type Props = {
  quests: Quest[];
  lookoutLevel: number;
  lookoutXp: number;
  questNotice: string;
  questCatalogRef: RefObject<HTMLDivElement | null>;
  busyQuestId: string | null;
  proofDrafts: Record<string, string>;
  setProofDrafts: (
    updater: (d: Record<string, string>) => Record<string, string>,
  ) => void;
  onAssignSeed: (seed: SeedQuest) => void;
  onSubmitProof: (id: string) => void;
  onConfirm: (id: string, isTrue: boolean) => void;
  onLeave: (id: string) => void;
  expandedActiveId: string | null;
  setExpandedActiveId: (id: string | null) => void;
  expandedCatalogId: string | null;
  setExpandedCatalogId: (id: string | null) => void;
  expandedDecade: number | null;
  setExpandedDecade: (n: number | null) => void;
  myActiveOpen: boolean;
  setMyActiveOpen: (open: boolean | ((o: boolean) => boolean)) => void;
  channelFilter: QuestChannel | "ALL";
  setChannelFilter: (ch: QuestChannel | "ALL") => void;
  assigneeFilter: "ALL" | "human" | "agent" | "either";
  setAssigneeFilter: (v: "ALL" | "human" | "agent" | "either") => void;
  missionFilter: string | "ALL";
  setMissionFilter: (v: string | "ALL") => void;
};

function questTypeGlyphSrc(quest: Quest): string {
  const t = `${quest.title} ${quest.description ?? ""}`.toLowerCase();
  if (/landing|use-case|industry|use case/.test(t)) {
    return "/brand/outpost16-quest-type-landing.png";
  }
  if (/\bvs\b|versus|competitor|compare/.test(t)) {
    return "/brand/outpost16-quest-type-compare.png";
  }
  if (/\btool\b|mini-tool|calculator|checklist/.test(t)) {
    return "/brand/outpost16-quest-type-tool.png";
  }
  return "/brand/outpost16-quest-assign-flag.png";
}

export function questStatusLabel(status: string): string {
  switch (status) {
    case "suggested":
      return "Suggested";
    case "accepted":
      return "Accepted";
    case "in_progress":
      return "In progress";
    case "proof_submitted":
      return "Ready to confirm";
    case "completed":
      return "Done";
    case "abandoned":
      return "Left";
    default:
      return status.replace(/_/g, " ");
  }
}

function seedPassesFilters(
  seed: SeedQuest,
  channelFilter: QuestChannel | "ALL",
  assigneeFilter: "ALL" | "human" | "agent" | "either",
  missionFilter: string | "ALL",
): boolean {
  if (channelFilter !== "ALL" && seed.channel !== channelFilter) return false;
  if (
    missionFilter !== "ALL" &&
    seed.missionId !== missionFilter
  ) {
    return false;
  }
  if (assigneeFilter === "ALL") return true;
  const by = seed.executableBy ?? "either";
  if (assigneeFilter === "either") return by === "either";
  return by === assigneeFilter;
}

export function QuestsPane({
  quests,
  lookoutLevel,
  lookoutXp,
  questNotice,
  questCatalogRef,
  busyQuestId,
  proofDrafts,
  setProofDrafts,
  onAssignSeed,
  onSubmitProof,
  onConfirm,
  onLeave,
  expandedActiveId,
  setExpandedActiveId,
  expandedCatalogId,
  setExpandedCatalogId,
  expandedDecade,
  setExpandedDecade,
  myActiveOpen,
  setMyActiveOpen,
  channelFilter,
  setChannelFilter,
  assigneeFilter,
  setAssigneeFilter,
  missionFilter,
  setMissionFilter,
}: Props) {
  const [refineOpen, setRefineOpen] = useState(false);
  const [catalogRail, setCatalogRail] = useState<CatalogRail>("ready");

  const xpProgress = lookoutXpProgress(lookoutXp);
  const currentDecade = decadeStart(lookoutLevel);
  const openDecade = expandedDecade ?? currentDecade;

  const activeQuests = useMemo(() => {
    return quests.filter((q) => {
      const statusOk =
        q.status === "accepted" ||
        q.status === "in_progress" ||
        q.status === "proof_submitted";
      if (!statusOk) return false;
      if (assigneeFilter === "ALL") return true;
      if (assigneeFilter === "agent") {
        return (
          q.assigneeKind === "agent" ||
          q.executableBy === "agent" ||
          q.completedBy === "agent"
        );
      }
      if (assigneeFilter === "human") {
        return (
          (q.assigneeKind === "human" || q.assigneeKind == null) &&
          q.executableBy !== "agent"
        );
      }
      return (q.executableBy || "either") === "either";
    });
  }, [quests, assigneeFilter]);

  const needsYou = useMemo(
    () => activeQuests.filter((q) => q.status === "proof_submitted"),
    [activeQuests],
  );

  const inMotion = useMemo(
    () =>
      activeQuests.filter(
        (q) => q.status === "accepted" || q.status === "in_progress",
      ),
    [activeQuests],
  );

  useEffect(() => {
    if (needsYou.length > 0) {
      setMyActiveOpen(true);
      setExpandedActiveId(needsYou[0]?.id ?? null);
    }
  }, [needsYou.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const refineActive =
    channelFilter !== "ALL" ||
    assigneeFilter !== "ALL" ||
    missionFilter !== "ALL";

  function classifySeed(seed: SeedQuest): CatalogRail {
    const existing = matchSeedInQuests(seed, quests);
    const parent = seed.requires
      ? SEED_QUEST_CATALOG.find((s) => s.id === seed.requires)
      : undefined;
    const chainBlocked =
      !!parent &&
      matchSeedInQuests(parent, quests)?.status !== "completed";
    const locked =
      isMinLevelLocked(seed.minLevel, lookoutLevel) || chainBlocked;
    if (existing?.status === "completed") return "done";
    if (
      existing &&
      (existing.status === "accepted" ||
        existing.status === "in_progress" ||
        existing.status === "proof_submitted")
    ) {
      return existing.status === "proof_submitted" ? "needs" : "ready";
    }
    if (locked) return "locked";
    if (existing?.status === "proof_submitted") return "needs";
    return "ready";
  }

  function renderActiveCard(q: Quest) {
    const busy = busyQuestId === q.id;
    const canProof =
      q.status === "accepted" || q.status === "in_progress";
    const canConfirm = q.status === "proof_submitted";
    const seed = findSeedForQuest(q);
    const open = expandedActiveId === q.id;
    return (
      <div
        key={q.id}
        id={`op-quest-${q.id}`}
        className={`op-quest-card${open ? " op-quest-card-open" : ""}${canConfirm ? " op-quest-card-needs" : ""}`}
      >
        <button
          type="button"
          className="op-quest-card-head"
          onClick={() => setExpandedActiveId(open ? null : q.id)}
        >
          <img
            className="op-quest-glyph op-quest-glyph-sway"
            src={questTypeGlyphSrc(q)}
            alt=""
            width={36}
            height={36}
          />
          <div className="op-item-copy">
            <h3>{q.title}</h3>
            <small>
              L{q.level}
              {seed?.automationLevel ? ` · ${seed.automationLevel}` : ""}
              {seed?.missionShortName
                ? ` · ${seed.missionShortName}`
                : ""}{" "}
              · {questStatusLabel(q.status)}
            </small>
          </div>
          <span
            className="op-xp-pill op-xp-reward"
            title="Awarded when you confirm this quest"
          >
            {seed ? questXp(seed) : XP_PER_QUEST} XP
          </span>
          <span className="op-tag">{open ? "Close" : "Open"}</span>
        </button>
        {open ? (
          <div className="op-quest-card-body">
            {seed ? (
              <>
                {seed.missionName ? (
                  <p className="op-quest-mission-meta">
                    {seed.missionName}
                    {seed.automationLevel
                      ? ` · ${seed.automationLevel}`
                      : ""}
                    {seed.timebox ? ` · ${seed.timebox}` : ""}
                  </p>
                ) : null}
                <p className="op-quest-brief">{questBrief(seed)}</p>
                <p className="op-quest-req-label">To complete</p>
                <ol className="op-quest-steps">
                  {questSteps(seed).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                {seed.successSignals?.length ? (
                  <p className="op-quest-signals">
                    Looks right when: {seed.successSignals.join("; ")}.
                  </p>
                ) : null}
                {seed.failureSignals?.length ? (
                  <p className="op-quest-signals">
                    Watch for: {seed.failureSignals.join("; ")}.
                  </p>
                ) : null}
                {seed.humanOnlyJudgment?.length ? (
                  <p className="op-quest-signals">
                    Your call: {seed.humanOnlyJudgment.join("; ")}.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="op-quest-brief">
                Finish the work, paste proof, confirm it is true.
              </p>
            )}
            {canProof ? (
              <div className="op-form-row" style={{ marginTop: 10 }}>
                <input
                  className="op-field"
                  value={proofDrafts[q.id] ?? ""}
                  onChange={(e) =>
                    setProofDrafts((d) => ({
                      ...d,
                      [q.id]: e.target.value,
                    }))
                  }
                  placeholder="Proof URL"
                />
                <button
                  type="button"
                  className="op-primary"
                  disabled={busy}
                  onClick={() => onSubmitProof(q.id)}
                >
                  Submit proof
                </button>
              </div>
            ) : null}
            {canConfirm ? (
              <div className="op-form-row" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="op-primary"
                  disabled={busy}
                  onClick={() => onConfirm(q.id, true)}
                >
                  Confirm true
                </button>
                <button
                  type="button"
                  className="op-btn"
                  disabled={busy}
                  onClick={() => onConfirm(q.id, false)}
                >
                  Not true
                </button>
              </div>
            ) : null}
            <div className="op-quest-card-actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="op-btn"
                disabled={busy}
                onClick={() => onLeave(q.id)}
              >
                Leave quest
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  function renderSeedCard(seed: SeedQuest) {
    const existing = matchSeedInQuests(seed, quests);
    const parent = seed.requires
      ? SEED_QUEST_CATALOG.find((s) => s.id === seed.requires)
      : undefined;
    const chainBlocked =
      !!parent &&
      matchSeedInQuests(parent, quests)?.status !== "completed";
    const locked =
      isMinLevelLocked(seed.minLevel, lookoutLevel) || chainBlocked;
    const done = existing?.status === "completed";
    const onBoard =
      existing &&
      (existing.status === "accepted" ||
        existing.status === "in_progress" ||
        existing.status === "proof_submitted");
    const busy = busyQuestId === seed.id;
    const open = expandedCatalogId === seed.id;
    return (
      <div
        key={seed.id}
        id={existing ? `op-quest-${existing.id}` : undefined}
        className={`op-quest-card${locked && !done ? " op-quest-locked" : ""}${open ? " op-quest-card-open" : ""}`}
      >
        <button
          type="button"
          className="op-quest-card-head"
          title={
            locked && !done
              ? chainBlocked
                ? `Finish ${seed.requires} first`
                : `Reach lookout L${seed.minLevel}`
              : "Open quest brief"
          }
          onClick={() => setExpandedCatalogId(open ? null : seed.id)}
        >
          <img
            className="op-quest-glyph"
            src={catalogGlyphSrc(seed, locked && !done)}
            alt=""
            width={36}
            height={36}
          />
          <div className="op-item-copy">
            <h3>
              <span className="op-quest-id">{seed.id}</span> {seed.title}
            </h3>
            <small>
              L{seed.minLevel}
              {seed.automationLevel ? ` · ${seed.automationLevel}` : ""}
              {seed.missionShortName
                ? ` · ${seed.missionShortName}`
                : ` · ${CHANNEL_LABELS[seed.channel]}`}
              {existing
                ? ` · ${questStatusLabel(existing.status)}`
                : locked
                  ? " · Locked"
                  : " · Ready"}
            </small>
          </div>
          <span className="op-xp-pill">{questXp(seed)} XP</span>
          <span className="op-tag">{open ? "Close" : "Open"}</span>
        </button>
        {open ? (
          <div className="op-quest-card-body">
            {seed.missionName ? (
              <p className="op-quest-mission-meta">
                {seed.missionName}
                {seed.automationLevel ? ` · ${seed.automationLevel}` : ""}
                {seed.timebox ? ` · ${seed.timebox}` : ""}
              </p>
            ) : null}
            <p className="op-quest-brief">{questBrief(seed)}</p>
            <p className="op-quest-req-label">To complete</p>
            <ol className="op-quest-steps">
              {questSteps(seed).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            {seed.successSignals?.length ? (
              <p className="op-quest-signals">
                Looks right when: {seed.successSignals.join("; ")}.
              </p>
            ) : null}
            {seed.failureSignals?.length ? (
              <p className="op-quest-signals">
                Watch for: {seed.failureSignals.join("; ")}.
              </p>
            ) : null}
            {seed.humanOnlyJudgment?.length ? (
              <p className="op-quest-signals">
                Your call: {seed.humanOnlyJudgment.join("; ")}.
              </p>
            ) : null}
            <div className="op-quest-card-actions">
              {done ? (
                <span className="op-tag">Done</span>
              ) : onBoard ? (
                <span className="op-tag">On your board</span>
              ) : (
                <button
                  type="button"
                  className="op-primary"
                  disabled={locked || busy}
                  onClick={() => onAssignSeed(seed)}
                >
                  Assign to me
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <section className="op-pane op-quests-pane">
      <div className="op-quests-stage">
        <div className="op-quests-embers" aria-hidden />
        <div className="op-quests-stage-head">
          <div>
            <h1>Quests</h1>
            <small>
              A little further, together · lookout L{lookoutLevel}/60
            </small>
          </div>
          <button
            type="button"
            className={refineOpen || refineActive ? "op-chip op-chip-on" : "op-chip"}
            aria-pressed={refineOpen}
            aria-expanded={refineOpen}
            onClick={() => setRefineOpen((o) => !o)}
          >
            Refine{refineActive ? " · on" : ""}
          </button>
        </div>

        <div className="op-quests-hero-meter" aria-label="Lookout XP progress">
          <div className="op-quests-hero-top">
            <div>
              <span className="op-quests-hero-level">L{lookoutLevel}</span>
              <span className="op-quests-hero-level-label">Lookout</span>
            </div>
            <div className="op-quests-hero-xp">
              <strong>{lookoutXp} XP</strong>
              <small>
                {xpProgress.nextLevel
                  ? `${xpProgress.xpToNext} to L${xpProgress.nextLevel}`
                  : "Max lookout"}
              </small>
            </div>
          </div>
          <div
            className="op-quests-meter-track"
            role="progressbar"
            aria-valuenow={Math.round(xpProgress.pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="op-quests-meter-fill"
              style={{ width: `${xpProgress.pct}%` }}
            />
          </div>
        </div>
      </div>

      {questNotice ? (
        <p className="op-sample" style={{ marginBottom: 12 }}>
          {questNotice}
        </p>
      ) : null}

      {refineOpen ? (
        <div className="op-quests-refine">
          <div className="op-channel-filters" role="tablist" aria-label="Channel">
            {CHANNEL_FILTERS.map((ch) => (
              <button
                key={ch}
                type="button"
                role="tab"
                aria-selected={channelFilter === ch}
                className={
                  channelFilter === ch ? "op-chip op-chip-on" : "op-chip"
                }
                onClick={() => setChannelFilter(ch)}
              >
                {ch === "ALL" ? "All" : CHANNEL_LABELS[ch]}
              </button>
            ))}
          </div>
          <div
            className="op-channel-filters"
            role="tablist"
            aria-label="Assignee"
          >
            {(
              [
                ["ALL", "All owners"],
                ["human", "Human"],
                ["agent", "Agent"],
                ["either", "Either"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={assigneeFilter === id}
                className={
                  assigneeFilter === id ? "op-chip op-chip-on" : "op-chip"
                }
                onClick={() => setAssigneeFilter(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div
            className="op-channel-filters"
            role="tablist"
            aria-label="Mission"
          >
            <button
              type="button"
              role="tab"
              aria-selected={missionFilter === "ALL"}
              className={
                missionFilter === "ALL" ? "op-chip op-chip-on" : "op-chip"
              }
              onClick={() => setMissionFilter("ALL")}
            >
              All
            </button>
            {GTM_MISSIONS.map((m) => (
              <button
                key={m.missionId}
                type="button"
                role="tab"
                aria-selected={missionFilter === m.missionId}
                className={
                  missionFilter === m.missionId
                    ? "op-chip op-chip-on"
                    : "op-chip"
                }
                onClick={() => setMissionFilter(m.missionId)}
              >
                {m.shortName}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {needsYou.length > 0 ? (
        <div className="op-quests-needs">
          <div className="op-section-title" style={{ marginTop: 0 }}>
            <h2>Needs you</h2>
            <span className="op-tag op-tag-ember">{needsYou.length}</span>
          </div>
          <div className="op-quests-gallery">
            {needsYou.map((q) => (
              <button
                key={q.id}
                type="button"
                className="op-quests-tile is-needs"
                onClick={() => {
                  setMyActiveOpen(true);
                  setExpandedActiveId(q.id);
                  window.requestAnimationFrame(() => {
                    document
                      .getElementById(`op-quest-${q.id}`)
                      ?.scrollIntoView({ behavior: "smooth", block: "center" });
                  });
                }}
              >
                <img
                  className="op-quest-glyph"
                  src={questTypeGlyphSrc(q)}
                  alt=""
                  width={32}
                  height={32}
                />
                <strong>{q.title}</strong>
                <small>Ready to confirm · {XP_PER_QUEST} XP</small>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="op-quests-board">
        <div className="op-my-active">
          <div className="op-section-title" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="op-collapse-toggle"
              onClick={() => setMyActiveOpen((o) => !o)}
              aria-expanded={myActiveOpen}
            >
              <h2>On your board</h2>
              <span className="op-tag">
                {myActiveOpen ? "Collapse" : "Expand"} · {activeQuests.length}
              </span>
            </button>
          </div>
          {myActiveOpen ? (
            <div className="op-list">
              {activeQuests.length === 0 ? (
                <div className="op-list-item">
                  <img
                    className="op-empty-icon"
                    src="/brand/outpost16-no-quests-compass.png"
                    alt=""
                    width={44}
                    height={44}
                  />
                  <div className="op-item-copy">
                    <h3>Board is clear</h3>
                    <small>
                      Assign from the open decade below. Fog stays closed until
                      you climb.
                    </small>
                  </div>
                </div>
              ) : (
                <>
                  {needsYou.map(renderActiveCard)}
                  {inMotion.map(renderActiveCard)}
                </>
              )}
            </div>
          ) : (
            <p className="op-quest-collapsed-hint">
              {activeQuests.length === 0
                ? "No active quests — open the catalog decade"
                : `${needsYou.length} ready to confirm · ${inMotion.length} in motion`}
            </p>
          )}
        </div>

        <div className="op-quests-catalog" ref={questCatalogRef}>
          <div className="op-section-title" style={{ marginTop: 0 }}>
            <h2>Catalog</h2>
            <span
              className="op-chain-waypoints"
              aria-hidden
              title="Chains unlock step by step"
            />
          </div>

          {allDecadeStarts().map((start) => {
            const end = decadeEnd(start);
            const isCurrent = start === currentDecade;
            const isNext = start === currentDecade + 10;
            const isFog = start > currentDecade + 10;
            const isOpen = openDecade === start;
            const seeds = SEED_QUEST_CATALOG.filter(
              (s) =>
                s.minLevel >= start &&
                s.minLevel <= end &&
                seedPassesFilters(
                  s,
                  channelFilter,
                  assigneeFilter,
                  missionFilter,
                ),
            );

            if (isFog) {
              return (
                <button
                  key={start}
                  type="button"
                  className="op-decade-row op-decade-fog"
                  disabled
                  title={`Reach L${start} to chart this range`}
                >
                  <span>
                    L{start}–{end} · Uncharted
                  </span>
                  <span className="op-tag">Fog</span>
                </button>
              );
            }

            if (!isOpen) {
              return (
                <button
                  key={start}
                  type="button"
                  className="op-decade-row"
                  onClick={() => {
                    setExpandedDecade(start);
                    setCatalogRail("ready");
                  }}
                >
                  <span>
                    L{start}–{end}
                    {isCurrent ? " · your band" : ""}
                    {isNext ? " · next" : ""}
                  </span>
                  <span className="op-tag">{seeds.length} quests</span>
                </button>
              );
            }

            const rails: Record<CatalogRail, SeedQuest[]> = {
              needs: [],
              ready: [],
              done: [],
              locked: [],
            };
            for (const seed of seeds) {
              const rail = classifySeed(seed);
              // On-board (accepted/in_progress) stay in ready rail as "on board"
              if (rail === "needs") rails.needs.push(seed);
              else if (rail === "done") rails.done.push(seed);
              else if (rail === "locked") rails.locked.push(seed);
              else rails.ready.push(seed);
            }

            const railSeeds = rails[catalogRail];
            const railMeta: { id: CatalogRail; label: string }[] = [
              { id: "needs", label: "Needs you" },
              { id: "ready", label: "Ready" },
              { id: "done", label: "Done" },
              { id: "locked", label: "Locked" },
            ];

            return (
              <div key={start} className="op-quest-level-block">
                <div className="op-section-title">
                  <h2>
                    L{start}–{end}
                    {isCurrent ? " · open" : ""}
                  </h2>
                  {!isCurrent ? (
                    <button
                      type="button"
                      className="op-btn"
                      onClick={() => setExpandedDecade(null)}
                    >
                      Back to band
                    </button>
                  ) : (
                    <span className="op-tag">{seeds.length}</span>
                  )}
                </div>

                <div
                  className="op-quests-rails"
                  role="tablist"
                  aria-label="Catalog rails"
                >
                  {railMeta.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      role="tab"
                      aria-selected={catalogRail === r.id}
                      className={
                        catalogRail === r.id
                          ? "op-quests-rail op-quests-rail-on"
                          : "op-quests-rail"
                      }
                      onClick={() => setCatalogRail(r.id)}
                    >
                      <strong>{rails[r.id].length}</strong>
                      <span>{r.label}</span>
                    </button>
                  ))}
                </div>

                <div className="op-list">
                  {railSeeds.length === 0 ? (
                    <div className="op-list-item">
                      <div className="op-item-copy">
                        <h3>Nothing in this rail</h3>
                        <small>
                          Try another rail, or clear Refine filters.
                        </small>
                      </div>
                    </div>
                  ) : (
                    railSeeds.map(renderSeedCard)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
